import type { ReactNode } from "react";
import Link from "next/link";
import { CircleAlert, Info } from "lucide-react";

export function AuthCard({
  title,
  description,
  children,
  footer,
}: {
  title: string;
  description: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <section className="w-full rounded-[18px] border border-[var(--m-line)] bg-white px-6 py-6 shadow-[0_18px_40px_-30px_rgba(16,40,80,0.45)] sm:px-8 sm:py-8">
      <h1 className="text-[1.875rem] leading-[1.15] font-semibold tracking-[-0.03em] text-[var(--m-ink)] sm:text-[2rem]">
        {title}
      </h1>
      <p className="mt-2 text-[15px] leading-6 text-[var(--m-muted)] sm:text-base">{description}</p>
      <div className="mt-6">{children}</div>
      {footer ? <div className="mt-6 text-sm text-[var(--m-muted)]">{footer}</div> : null}
    </section>
  );
}

export function AuthLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <Link
      href={href}
      className="font-semibold text-[var(--m-accent)] underline-offset-4 hover:text-[var(--m-accent-hover)] hover:underline focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--m-accent)]"
    >
      {children}
    </Link>
  );
}

export const authPrimaryClass =
  "inline-flex h-12 w-full cursor-pointer items-center justify-center rounded-[10px] bg-[var(--m-accent)] px-4 text-[15px] font-semibold text-white transition-colors hover:bg-[var(--m-accent-hover)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--m-accent)] disabled:cursor-not-allowed disabled:opacity-60";

export const authFieldClassName =
  "h-12 w-full rounded-[10px] border border-[#d5deea] bg-white px-3.5 text-base text-[var(--m-ink)] outline-none transition placeholder:text-[var(--m-muted)] focus-visible:border-[var(--m-accent)] focus-visible:ring-3 focus-visible:ring-[rgba(11,99,246,0.16)] aria-invalid:border-[#d45353] aria-invalid:ring-3 aria-invalid:ring-[rgba(212,83,83,0.14)]";

export const fieldClassName =
  "h-11 w-full rounded-lg border border-input bg-background px-3 text-base outline-none transition focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/30 aria-invalid:border-destructive";

export function AuthField({
  label,
  htmlFor,
  error,
  hint,
  aside,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string;
  hint?: string;
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-3">
        <label htmlFor={htmlFor} className="text-sm font-medium text-[var(--m-ink)]">
          {label}
        </label>
        {aside}
      </div>
      {children}
      {error ? (
        <p id={`${htmlFor}-error`} className="text-[13px] leading-5 text-[#9f2d2d]" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p id={`${htmlFor}-hint`} className="text-[13px] leading-5 text-[var(--m-muted)]">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export function AuthStatus({
  tone,
  children,
}: {
  tone: "error" | "info";
  children: ReactNode;
}) {
  const icon =
    tone === "error" ? (
      <CircleAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
    ) : (
      <Info className="mt-0.5 size-4 shrink-0 text-[var(--m-accent)]" aria-hidden="true" />
    );
  const surface =
    tone === "error"
      ? "border-[#f0d0d0] bg-[#fff6f6] text-[#8f2d2d]"
      : "border-[#d5e4fb] bg-[var(--m-soft)] text-[var(--m-ink)]";

  return (
    <div role={tone === "error" ? "alert" : "status"} className={`flex gap-2.5 rounded-[10px] border px-3 py-2.5 text-[13px] leading-5 ${surface}`}>
      {icon}
      <div>{children}</div>
    </div>
  );
}

export function Field({
  label,
  htmlFor,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={htmlFor} className="text-sm font-medium text-[#071936]">
        {label}
      </label>
      {children}
      {error ? (
        <p id={`${htmlFor}-error`} className="text-[13px] text-[#d1263a]" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
